import React, { useState, useCallback } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useUserAuth } from "../context/userAuthContext";
import { useSecurityNotifications } from "../components/Finance/Mail/hooks/useSecutityNotifications";
import {
  ArrowRight,
  AlertCircle,
  CheckCircle,
  Eye,
  EyeOff,
  Building2,
  Mail,
  Lock,
  Shield,
  TrendingUp,
  BarChart3,
  Zap,
  Sparkles,
  Star,
  Users,
  ArrowLeft,
  X,
} from "lucide-react";

const MiguelMXLogin = () => {
  const [formData, setFormData] = useState({
    email: "",
    password: "",
  });
  const [uiState, setUiState] = useState({
    error: "",
    success: "",
    showPassword: false,
    isLoading: false,
  });

  const { logIn, googleSignIn } = useUserAuth();
  const { sendLoginNotification, loading: notificationLoading } =
    useSecurityNotifications();
  const navigate = useNavigate();

  // Validación de email optimizada
  const validateEmail = useCallback((email) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  }, []);

  // Handler para cambios en inputs
  const handleInputChange = useCallback(
    (field, value) => {
      setFormData((prev) => ({ ...prev, [field]: value }));
      if (uiState.error) {
        setUiState((prev) => ({ ...prev, error: "" }));
      }
    },
    [uiState.error]
  );

  // Toggle password visibility
  const togglePasswordVisibility = useCallback(() => {
    setUiState((prev) => ({ ...prev, showPassword: !prev.showPassword }));
  }, []);

  // Función para enviar notificación de login
  const sendSecurityNotification = async (userEmail, userName, loginMethod) => {
    try {
      console.log("Enviando notificación de seguridad...");
      await sendLoginNotification(userEmail, userName, loginMethod);
      console.log("Notificación de seguridad enviada correctamente");
    } catch (error) {
      console.error("Error enviando notificación de seguridad:", error);
      // No bloqueamos el login por un error en la notificación
    }
  };

  // Handler para login con email/password
  const handleSubmit = useCallback(
    async (e) => {
      e.preventDefault();

      const { email, password } = formData;

      // Resetear estados
      setUiState((prev) => ({
        ...prev,
        error: "",
        success: "",
        isLoading: true,
      }));

      // Validaciones
      if (!email.trim()) {
        setUiState((prev) => ({
          ...prev,
          error: "Por favor ingrese su correo electrónico",
          isLoading: false,
        }));
        return;
      }

      if (!validateEmail(email)) {
        setUiState((prev) => ({
          ...prev,
          error: "Por favor ingrese un correo electrónico válido",
          isLoading: false,
        }));
        return;
      }

      if (!password.trim()) {
        setUiState((prev) => ({
          ...prev,
          error: "Por favor ingrese su contraseña",
          isLoading: false,
        }));
        return;
      }

      try {
        // Iniciar sesión
        const userCredential = await logIn(email, password);
        const user = userCredential.user;

        setUiState((prev) => ({
          ...prev,
          success:
            "¡Inicio de sesión exitoso! Enviando notificación de seguridad...",
          isLoading: false,
        }));

        // Enviar notificación de seguridad (sin bloquear el flujo)
        sendSecurityNotification(
          user.email,
          user.displayName || user.email.split("@")[0],
          "email"
        );

        // Esperar un momento para mostrar el mensaje
        setTimeout(() => {
          setUiState((prev) => ({
            ...prev,
            success: "¡Redirigiendo al dashboard...",
          }));
          setTimeout(() => navigate("/dashboard"), 1000);
        }, 1500);
      } catch (err) {
        setUiState((prev) => ({
          ...prev,
          error: err.message,
          isLoading: false,
        }));
      }
    },
    [formData, logIn, navigate, validateEmail, sendLoginNotification]
  );

  // Handler para login con Google
  const handleGoogleSignIn = useCallback(
    async (e) => {
      e.preventDefault();
      setUiState((prev) => ({ ...prev, error: "", isLoading: true }));

      try {
        // Iniciar sesión con Google
        const userCredential = await googleSignIn();
        const user = userCredential.user;

        setUiState((prev) => ({
          ...prev,
          success:
            "¡Inicio de sesión con Google exitoso! Enviando notificación de seguridad...",
          isLoading: false,
        }));

        // Enviar notificación de seguridad (sin bloquear el flujo)
        sendSecurityNotification(
          user.email,
          user.displayName || user.email.split("@")[0],
          "google"
        );

        // Esperar un momento para mostrar el mensaje
        setTimeout(() => {
          setUiState((prev) => ({
            ...prev,
            success: "¡Redirigiendo al dashboard...",
          }));
          setTimeout(() => navigate("/dashboard"), 1000);
        }, 1500);
      } catch (error) {
        console.error("Google Sign-In Error:", error.message);
        setUiState((prev) => ({
          ...prev,
          error: "Error al iniciar sesión con Google",
          isLoading: false,
        }));
      }
    },
    [googleSignIn, navigate, sendLoginNotification]
  );

  // Componente para alertas
  const AlertMessage = ({ type, message }) => {
    const isError = type === "error";
    const bgColor = isError ? "bg-red-50" : "bg-green-50";
    const borderColor = isError ? "border-red-200" : "border-green-200";
    const textColor = isError ? "text-red-700" : "text-green-700";
    const IconComponent = isError ? AlertCircle : CheckCircle;
    const iconColor = isError ? "text-red-500" : "text-green-500";

    return (
      <div
        className={`${bgColor} border ${borderColor} ${textColor} p-3 rounded-lg flex items-center space-x-2 text-sm animate-in slide-in-from-top duration-300`}
      >
        <IconComponent className={`w-4 h-4 flex-shrink-0 ${iconColor}`} />
        <span className="font-medium">{message}</span>
        {notificationLoading && type === "success" && (
          <div className="ml-auto">
            <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-green-500"></div>
          </div>
        )}
      </div>
    );
  };

  // Features para el panel lateral
  const features = [
    {
      icon: BarChart3,
      title: "Analytics Avanzado",
      text: "Dashboard ejecutivo con análisis en tiempo real",
    },
    {
      icon: TrendingUp,
      title: "Gestión de Portafolio",
      text: "Administración integral de inversiones y seguros",
    },
    {
      icon: Zap,
      title: "Automatización IA",
      text: "Planificación financiera automatizada",
    },
    {
      icon: Shield,
      title: "Seguridad Bancaria",
      text: "Certificaciones SSL y encriptación AES256",
    },
  ];

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-white flex">
      {/* Left Side - Form (50% width on desktop) */}
      <div className="flex-1 lg:w-1/2 flex flex-col">
        {/* Top Navigation Bar */}
        <div className="bg-white/80 backdrop-blur-sm border-b border-slate-200 px-4 py-3 sticky top-0 z-20">
          <div className="flex items-center justify-between">
            <button
              onClick={() => navigate("/")}
              className="flex items-center text-slate-600 hover:text-[#14840c] transition-colors p-2 -ml-2 rounded-lg hover:bg-slate-100"
            >
              <ArrowLeft className="w-5 h-5" />
            </button>
            <h1 className="text-lg font-bold text-slate-900">Iniciar Sesión</h1>
            <div className="w-9" /> {/* Spacer for alignment */}
          </div>
        </div>

        {/* Main Content - Centered */}
        <div className="flex-1 px-4 py-6 overflow-y-auto flex items-center justify-center">
          <div className="w-full max-w-sm lg:max-w-md mx-auto space-y-6">
            {/* Logo */}
            <div className="text-center">
              <div className="relative inline-block">
                <img
                  src="https://firebasestorage.googleapis.com/v0/b/xfinance-12e10.firebasestorage.app/o/Logos%2FLogo%20verde%20(1)%20(2).png?alt=media&token=8d468575-6d03-4ca0-b99c-0619c8a175ef"
                  alt="MiguelMX"
                  className="h-12 lg:h-16 w-auto object-contain mx-auto"
                  onError={(e) => {
                    e.target.style.display = "none";
                    e.target.nextSibling.style.display = "flex";
                  }}
                />
                <div className="w-40 h-12 bg-gradient-to-r from-[#14840c] to-green-700 rounded-lg items-center justify-center shadow-lg hidden">
                  <span className="text-white font-bold text-lg">
                    MiguelMX®
                  </span>
                </div>
                <div className="absolute -top-1 -right-1 w-4 h-4 bg-gradient-to-r from-green-400 to-emerald-500 rounded-full flex items-center justify-center">
                  <Sparkles className="w-2 h-2 text-white" />
                </div>
              </div>
              <h2 className="text-2xl lg:text-3xl font-black text-slate-900 mt-4 mb-2">
                ¡Bienvenido!
              </h2>
              <p className="text-slate-600 text-sm lg:text-base">
                Accede a tu plataforma financiera
              </p>
            </div>

            {/* Alert Messages */}
            {uiState.error && (
              <AlertMessage type="error" message={uiState.error} />
            )}
            {uiState.success && (
              <AlertMessage type="success" message={uiState.success} />
            )}

            {/* Security Notice */}
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
              <div className="flex items-start space-x-2">
                <Shield className="w-4 h-4 text-blue-500 mt-0.5 flex-shrink-0" />
                <div className="text-sm">
                  <p className="text-blue-800 font-medium">
                    Notificaciones de Seguridad
                  </p>
                  <p className="text-blue-600 text-xs mt-1">
                    Recibirás un email con los detalles de este inicio de sesión
                    para tu seguridad.
                  </p>
                </div>
              </div>
            </div>

            {/* Login Form */}
            <form onSubmit={handleSubmit} className="space-y-4 lg:space-y-5">
              {/* Email Field */}
              <div>
                <label className="block text-sm font-semibold text-slate-700 mb-2">
                  Correo Electrónico *
                </label>
                <div className="relative">
                  <Mail className="absolute left-3 top-1/2 transform -translate-y-1/2 text-slate-400 w-4 h-4 lg:w-5 lg:h-5" />
                  <input
                    type="email"
                    value={formData.email}
                    onChange={(e) => handleInputChange("email", e.target.value)}
                    className="w-full pl-10 lg:pl-12 pr-3 py-3 lg:py-4 border border-slate-300 rounded-lg focus:ring-2 focus:ring-[#14840c] focus:border-[#14840c] transition-all placeholder-slate-400 text-sm lg:text-base bg-white"
                    placeholder="usuario@empresa.com"
                    required
                  />
                </div>
              </div>

              {/* Password Field */}
              <div>
                <label className="block text-sm font-semibold text-slate-700 mb-2">
                  Contraseña *
                </label>
                <div className="relative">
                  <Lock className="absolute left-3 top-1/2 transform -translate-y-1/2 text-slate-400 w-4 h-4 lg:w-5 lg:h-5" />
                  <input
                    type={uiState.showPassword ? "text" : "password"}
                    value={formData.password}
                    onChange={(e) =>
                      handleInputChange("password", e.target.value)
                    }
                    className="w-full pl-10 lg:pl-12 pr-10 lg:pr-12 py-3 lg:py-4 border border-slate-300 rounded-lg focus:ring-2 focus:ring-[#14840c] focus:border-[#14840c] transition-all placeholder-slate-400 text-sm lg:text-base bg-white"
                    placeholder="Tu contraseña"
                    required
                  />
                  <button
                    type="button"
                    onClick={togglePasswordVisibility}
                    className="absolute right-3 top-1/2 transform -translate-y-1/2 text-slate-400 hover:text-slate-600 transition-colors"
                  >
                    {uiState.showPassword ? (
                      <EyeOff className="w-4 h-4 lg:w-5 lg:h-5" />
                    ) : (
                      <Eye className="w-4 h-4 lg:w-5 lg:h-5" />
                    )}
                  </button>
                </div>
              </div>

              {/* Forgot Password */}
              <div className="text-right">
                <Link
                  to="/forgot-password"
                  className="text-xs lg:text-sm text-[#14840c] hover:text-green-700 font-semibold"
                >
                  ¿Olvidaste tu contraseña?
                </Link>
              </div>

              {/* Submit Button */}
              <button
                type="submit"
                disabled={uiState.isLoading || notificationLoading}
                className="w-full flex justify-center items-center py-3 lg:py-4 px-4 rounded-lg text-white bg-gradient-to-r from-[#14840c] to-green-600 hover:from-green-600 hover:to-green-700 font-semibold transition-all duration-300 disabled:opacity-50 transform hover:scale-105 shadow-lg"
              >
                {uiState.isLoading ? (
                  <div className="flex items-center">
                    <div className="animate-spin rounded-full h-4 w-4 lg:h-5 lg:w-5 border-b-2 border-white mr-2" />
                    <span className="text-sm lg:text-base">Iniciando...</span>
                  </div>
                ) : (
                  <div className="flex items-center">
                    <span className="text-sm lg:text-base font-bold">
                      Iniciar Sesión
                    </span>
                    <ArrowRight className="ml-2 w-4 h-4 lg:w-5 lg:h-5" />
                  </div>
                )}
              </button>
            </form>

            {/* Divider */}
            <div className="relative">
              <div className="absolute inset-0 flex items-center">
                <div className="w-full border-t border-slate-200" />
              </div>
              <div className="relative flex justify-center text-xs lg:text-sm">
                <span className="px-2 bg-gradient-to-br from-slate-50 to-white text-slate-500 font-medium">
                  O continúa con
                </span>
              </div>
            </div>

            {/* Google Sign In */}
            <button
              onClick={handleGoogleSignIn}
              disabled={uiState.isLoading || notificationLoading}
              className="w-full flex justify-center items-center py-3 lg:py-4 px-4 border border-slate-300 rounded-lg bg-white hover:bg-slate-50 font-semibold text-slate-700 transition-all duration-300 transform hover:scale-105 shadow-sm"
            >
              <svg className="w-4 h-4 lg:w-5 lg:h-5 mr-2" viewBox="0 0 24 24">
                <path
                  fill="#4285F4"
                  d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
                />
                <path
                  fill="#34A853"
                  d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
                />
                <path
                  fill="#FBBC05"
                  d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
                />
                <path
                  fill="#EA4335"
                  d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
                />
              </svg>
              <span className="text-sm lg:text-base">Continuar con Google</span>
            </button>

            {/* Sign Up Link */}
            <div className="text-center pt-4">
              <p className="text-slate-600 text-sm lg:text-base">
                ¿Nuevo en MiguelMX?{" "}
                <Link
                  to="/signup"
                  className="text-[#14840c] hover:text-green-700 font-bold"
                >
                  Crear Cuenta
                </Link>
              </p>
            </div>

            {/* Trust Indicators */}
          </div>
        </div>
      </div>

      {/* Desktop: Right Side Panel (50% width) */}
      <div className="hidden lg:flex lg:w-1/2 bg-gradient-to-br from-[#14840c] via-green-600 to-emerald-700 relative overflow-hidden">
        {/* Animated Background Elements */}
        <div className="absolute inset-0 overflow-hidden">
          <div className="absolute top-20 right-20 w-32 h-32 border-2 border-white/20 rounded-full animate-pulse" />
          <div className="absolute bottom-32 right-40 w-24 h-24 border-2 border-white/30 rounded-full animate-bounce" />
          <div className="absolute top-1/2 right-12 w-16 h-16 border-2 border-white/25 rounded-full animate-ping" />
          <div className="absolute top-40 right-60 w-20 h-20 bg-white/10 rounded-full backdrop-blur-sm" />
          <div className="absolute bottom-20 right-20 w-12 h-12 bg-white/15 rounded-full" />
        </div>

        <div className="flex-1 flex flex-col justify-center px-12 relative z-10">
          <div className="max-w-lg">
            {/* Brand Header */}
            <div className="flex items-center mb-10">
              <div className="w-20 h-20 bg-white/15 backdrop-blur-sm rounded-2xl flex items-center justify-center mr-5 border border-white/30 shadow-2xl">
                <img
                  src="https://firebasestorage.googleapis.com/v0/b/xfinance-12e10.firebasestorage.app/o/Logos%2Flogo192.png?alt=media&token=0dd60c18-dff8-4b94-ad27-b31256efd17f"
                  alt="MiguelMX App"
                  className="h-12 w-12 object-contain"
                  onError={(e) => {
                    e.target.style.display = "none";
                    e.target.nextSibling.style.display = "flex";
                  }}
                />
                <Building2 className="w-12 h-12 text-white hidden" />
              </div>
              <div>
                <h3 className="text-3xl font-black text-white">MiguelMX®</h3>
                <p className="text-green-200 font-bold text-lg">
                  Fintech Inteligente
                </p>
              </div>
            </div>

            {/* Main Message */}
            <h2 className="text-5xl font-black text-white mb-8 leading-tight">
              Tu futuro financiero{" "}
              <span className="text-green-200 block">empieza hoy.</span>
            </h2>

            <p className="text-xl text-green-50 mb-10 leading-relaxed font-medium">
              Plataforma integral de gestión financiera con IA, análisis
              predictivo y herramientas profesionales para maximizar tu
              patrimonio.
            </p>

            {/* Features */}
            <div className="space-y-6">
              {features.map((feature, index) => (
                <div
                  key={index}
                  className="flex items-start space-x-4 group hover:translate-x-2 transition-transform duration-300"
                >
                  <div className="flex-shrink-0 w-12 h-12 bg-white/20 rounded-xl flex items-center justify-center border border-white/30 shadow-lg group-hover:bg-white/30 transition-colors">
                    <feature.icon className="w-6 h-6 text-green-200" />
                  </div>
                  <div>
                    <h4 className="text-white font-bold text-lg mb-1">
                      {feature.title}
                    </h4>
                    <span className="text-green-100 font-medium leading-relaxed">
                      {feature.text}
                    </span>
                  </div>
                </div>
              ))}
            </div>

            {/* Security Feature Highlight */}
            <div className="mt-12 p-6 bg-white/10 backdrop-blur-sm rounded-2xl border border-white/20">
              <div className="flex items-center mb-3">
                <Shield className="w-8 h-8 text-green-200 mr-3" />
                <h4 className="text-white font-bold text-xl">
                  Seguridad Avanzada
                </h4>
              </div>
              <p className="text-green-100 leading-relaxed">
                Notificaciones automáticas de inicio de sesión, detección de
                ubicación e información detallada del dispositivo para mantener
                tu cuenta segura.
              </p>
            </div>

            {/* Footer */}
            <div className="mt-16 pt-8 border-t border-green-400/30">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-green-200 text-sm font-bold">
                    Desarrollado por fronios.com
                  </p>
                  <p className="text-green-300 text-xs mt-1">
                    © 2025 MiguelMX®. Todos los derechos reservados.
                  </p>
                </div>
                <div className="flex items-center space-x-2">
                  <div className="w-2 h-2 bg-green-400 rounded-full animate-pulse" />
                  <span className="text-green-200 text-sm font-medium">
                    En línea
                  </span>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Gradient Overlays */}
        <div className="absolute inset-0 bg-gradient-to-t from-emerald-800/30 to-transparent" />
        <div className="absolute inset-0 bg-gradient-to-r from-green-900/20 to-transparent" />
      </div>
    </div>
  );
};

export default MiguelMXLogin;
